const page = document.body;
const params = new URLSearchParams(window.location.search);
const cartDrawer = document.querySelector('#cart-drawer');
const cartItems = cartDrawer?.querySelector('.c-cart-drawer__items');
const cartToggle = document.querySelector('.js-cart-toggle');
const cartBackdrop = document.querySelector('.js-cart-backdrop');
const siteMenu = document.querySelector('#site-menu');
const menuToggle = document.querySelector('.js-menu-toggle');
const addToCart = document.querySelector('.js-add-to-cart');
const productForm = document.querySelector('.js-product-form');
const quickBuyButtons = [...document.querySelectorAll('.js-quick-buy')];
const localizationForms = [...document.querySelectorAll('.js-localization-form')];
const menuBackground = [...document.querySelectorAll('main, .c-global-footer')];
const overlayCloseStates = new WeakMap();
const numberSwitchErrorTimers = new WeakMap();
const runningRibbonStates = new WeakMap();
const overlayViewportProperty = '--size-overlay-viewport';
let lastOverlayTrigger = null;

function getFocusable(container) {
  if (!container) return [];
  return [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hasAttribute('inert') && element.getClientRects().length > 0);
}

function focusFirstOverlayControl(container, motionElement = container) {
  let attempts = 0;
  const focus = () => {
    if (
      !container
      || container.hasAttribute('inert')
      || container.getAttribute('aria-hidden') === 'true'
      || container.contains(document.activeElement)
    ) return;
    const target = getFocusable(container)[0];
    target?.focus({ preventScroll: true });
    attempts += 1;
    if (!container?.contains(document.activeElement) && attempts < 12) {
      window.requestAnimationFrame(focus);
    }
  };
  window.requestAnimationFrame(focus);
  window.setTimeout(focus, maxTransitionMilliseconds(motionElement) + 50);
  window.setTimeout(focus, maxTransitionMilliseconds(motionElement) + 250);
  if (document.readyState !== 'complete') {
    window.addEventListener('load', () => window.requestAnimationFrame(focus), { once: true });
  }
}

function isOverlayActive(element) {
  return Boolean(
    element?.classList.contains('is-open') || element?.classList.contains('is-closing'),
  );
}

function cssTimeToMilliseconds(value) {
  const time = Number.parseFloat(value);
  if (!Number.isFinite(time)) return 0;
  return value.trim().endsWith('ms') ? time : time * 1000;
}

function maxTransitionMilliseconds(element) {
  const style = getComputedStyle(element);
  const durations = style.transitionDuration.split(',').map(cssTimeToMilliseconds);
  const delays = style.transitionDelay.split(',').map(cssTimeToMilliseconds);
  return Math.max(0, ...durations.map((duration, index) => (
    duration + (delays[index % delays.length] ?? 0)
  )));
}

function cancelOverlayClose(shell) {
  overlayCloseStates.get(shell)?.cancel();
  shell.classList.remove('is-closing');
}

function finishOverlayCloseAfterTransition(shell, motionElement, onComplete) {
  let active = true;
  let timeoutId;

  const cleanUpListener = () => {
    motionElement.removeEventListener('transitionend', handleTransitionEnd);
    window.clearTimeout(timeoutId);
  };
  const finish = () => {
    if (!active) return;
    active = false;
    cleanUpListener();
    overlayCloseStates.delete(shell);
    shell.classList.remove('is-closing');
    syncOverlayState();
    onComplete();
  };
  const handleTransitionEnd = (event) => {
    if (event.target === motionElement && event.propertyName === 'transform') finish();
  };

  motionElement.addEventListener('transitionend', handleTransitionEnd);
  timeoutId = window.setTimeout(
    finish,
    maxTransitionMilliseconds(motionElement) + 50,
  );
  overlayCloseStates.set(shell, {
    cancel() {
      if (!active) return;
      active = false;
      cleanUpListener();
      overlayCloseStates.delete(shell);
    },
  });
}

function syncOverlayState() {
  const hasOverlay = isOverlayActive(siteMenu) || isOverlayActive(cartDrawer);
  const root = document.documentElement;
  if (hasOverlay && !root.style.getPropertyValue(overlayViewportProperty)) {
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (Number.isFinite(viewportHeight) && viewportHeight > 0) {
      const stableHeight = Math.round(viewportHeight * 100) / 100;
      root.style.setProperty(overlayViewportProperty, `${stableHeight}px`);
    }
  } else if (!hasOverlay) {
    root.style.removeProperty(overlayViewportProperty);
  }
  root.classList.toggle('has-overlay-open', hasOverlay);
  menuBackground.forEach((element) => {
    if (hasOverlay) element.setAttribute('inert', '');
    else element.removeAttribute('inert');
  });
}

function setCartPresence(hasItems, itemCount = hasItems ? 1 : 0) {
  page.classList.toggle('has-cart-items', hasItems);
  document.querySelectorAll('.js-cart-shipping-shell').forEach((shell) => {
    shell.classList.toggle('is-empty', !hasItems);
  });
  if (cartToggle) {
    cartToggle.setAttribute('aria-label', hasItems ? `Open cart, ${itemCount} items` : 'Open cart, empty');
  }
}

function formatShippingAmount(amountMinor, currencyIso) {
  const amount = Number(amountMinor) / 100;
  const currency = currencyIso || window.stufTheme?.currencyIso || 'USD';
  try {
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted} ${currency}`;
  } catch {
    return `${formatMoney(amountMinor)} ${currency}`;
  }
}

function setShippingProgressLoading(loading) {
  document.querySelectorAll('.js-shipping-progress').forEach((progress) => {
    progress.classList.toggle('is-loading', loading);
    progress.toggleAttribute('aria-busy', loading);
  });
}

function updateShippingProgress(cart) {
  const hasItems = Number(cart?.item_count ?? 0) > 0;
  setCartPresence(hasItems, Number(cart?.item_count ?? 0));
  document.querySelectorAll('.js-shipping-progress').forEach((progress) => {
    const mode = progress.dataset.shippingMode;
    const message = progress.querySelector('.js-shipping-progress-message');
    const fill = progress.querySelector('.c-shipping-progress__fill');
    let state = mode === 'complete' ? 'complete' : 'calculated';
    let label = progress.dataset.calculatedMessage || 'THAILAND POST EMS WORLD — CALCULATED AT CHECKOUT';
    let percent = mode === 'complete' ? 100 : 0;

    if (mode === 'threshold') {
      const threshold = Number.parseInt(progress.dataset.thresholdMinor ?? '0', 10);
      const total = Math.max(0, Number(cart?.total_price ?? 0));
      if (threshold > 0 && total >= threshold) {
        state = 'complete';
        percent = 100;
        label = progress.dataset.completeMessage || 'FREE EMS SHIPPING UNLOCKED';
      } else if (threshold > 0) {
        state = 'progress';
        percent = Math.min(100, Math.max(0, (total / threshold) * 100));
        const remaining = Math.max(0, threshold - total);
        const amount = formatShippingAmount(remaining, progress.dataset.currencyIso);
        label = (progress.dataset.progressTemplate || 'ADD [amount] FOR FREE EMS SHIPPING')
          .replace('[amount]', amount);
      }
    } else if (mode === 'complete') {
      label = message?.textContent?.trim() || label;
    }

    progress.classList.toggle('is-progress', state === 'progress');
    progress.classList.toggle('is-complete', state === 'complete');
    progress.classList.toggle('is-calculated', state === 'calculated');
    progress.classList.remove('is-loading');
    progress.removeAttribute('aria-busy');
    if (message) message.textContent = label;
    fill?.style.setProperty('--c-shipping-progress-value', `${percent}%`);
  });
}

function syncLocalizationOptions(form) {
  form.querySelectorAll('.js-localization-option').forEach((option) => {
    option.classList.toggle(
      'is-selected',
      Boolean(option.querySelector('.js-localization-radio')?.checked),
    );
  });
}

function syncLocalizationOverflow(form) {
  form.querySelectorAll('.c-localization-form__trigger-label, .c-localization-form__option-text')
    .forEach((label) => {
      label.classList.toggle('has-overflow', label.scrollWidth > label.clientWidth + 1);
    });
}

function resetLocalizationSelection(form) {
  const current = form.querySelector('.js-localization-radio[data-localization-current="true"]');
  if (current) current.checked = true;
  syncLocalizationOptions(form);
}

function syncLocalizationShell() {
  siteMenu?.classList.toggle(
    'has-localization-open',
    localizationForms.some((form) => form.classList.contains('is-expanded')),
  );
}

function setLocalizationExpanded(form, expanded, { restoreFocus = true, reset = false } = {}) {
  if (!form) return;
  const trigger = form.querySelector('.js-localization-trigger');
  const panel = form.querySelector('.js-localization-panel');
  if (!trigger || !panel) return;
  if (!expanded && reset) resetLocalizationSelection(form);
  form.classList.toggle('is-expanded', expanded);
  trigger.setAttribute('aria-expanded', String(expanded));
  trigger.hidden = expanded;
  panel.hidden = !expanded;
  syncLocalizationShell();
  if (expanded) {
    const selected = form.querySelector('.js-localization-radio:checked');
    window.requestAnimationFrame(() => {
      syncLocalizationOverflow(form);
      selected?.focus({ preventScroll: true });
    });
  } else if (restoreFocus) {
    window.requestAnimationFrame(() => {
      syncLocalizationOverflow(form);
      trigger.focus({ preventScroll: true });
    });
  } else {
    window.requestAnimationFrame(() => syncLocalizationOverflow(form));
  }
}

function collapseLocalizationForms({ restoreFocus = false, reset = true } = {}) {
  localizationForms.forEach((form) => {
    if (form.classList.contains('is-expanded')) {
      setLocalizationExpanded(form, false, { restoreFocus, reset });
    }
  });
}

function initializeRunningRibbon(ribbon) {
  if (runningRibbonStates.has(ribbon)) return;
  const viewport = ribbon.querySelector('.js-running-ribbon-viewport');
  const track = ribbon.querySelector('.js-running-ribbon-track');
  const group = ribbon.querySelector('.js-running-ribbon-group');
  const itemTemplate = ribbon.querySelector('.js-running-ribbon-item');
  if (!viewport || !track || !group || !(itemTemplate instanceof HTMLTemplateElement)) return;

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverQuery = window.matchMedia('(hover: hover)');
  let resizeFrame;
  let hoverRateFrame;
  let hoverSlowed = false;

  const applyHoverPlaybackRate = () => {
    window.cancelAnimationFrame(hoverRateFrame);
    hoverRateFrame = window.requestAnimationFrame(() => {
      hoverRateFrame = undefined;
      const configuredHoverRate = Number.parseFloat(
        getComputedStyle(ribbon).getPropertyValue('--motion-running-ribbon-hover-rate'),
      );
      const hoverRate = Number.isFinite(configuredHoverRate)
        && configuredHoverRate > 0
        && configuredHoverRate < 1
        ? configuredHoverRate
        : 1;
      const shouldSlow = hoverSlowed && hoverQuery.matches;
      const playbackRate = shouldSlow ? hoverRate : 1;

      ribbon.classList.toggle('is-hover-slow', shouldSlow);
      ribbon.dataset.ribbonPlaybackRate = String(playbackRate);
      track.getAnimations().forEach((animation) => {
        if (animation.animationName && animation.animationName !== 'c-running-ribbon-loop') return;
        /* motion: updatePlaybackRate preserves the loop phase; changing CSS duration can snap the track. */
        if (typeof animation.updatePlaybackRate === 'function') {
          animation.updatePlaybackRate(playbackRate);
        } else {
          animation.playbackRate = playbackRate;
        }
      });
    });
  };

  const showStaticFallback = () => {
    window.cancelAnimationFrame(hoverRateFrame);
    hoverRateFrame = undefined;
    track.replaceChildren(group);
    group.replaceChildren();
    track.style.removeProperty('--c-running-ribbon-distance');
    track.style.removeProperty('--c-running-ribbon-duration');
    ribbon.classList.remove('is-running', 'is-paused', 'is-hover-slow');
    ribbon.dataset.ribbonMode = 'static';
    ribbon.dataset.ribbonPlaybackRate = '0';
    ribbon.dataset.ribbonReady = 'true';
  };

  const buildLoop = () => {
    resizeFrame = undefined;
    const animationEnabled = ribbon.dataset.ribbonAnimate !== 'false';
    if (!animationEnabled || motionQuery.matches) {
      showStaticFallback();
      return;
    }

    ribbon.classList.remove('is-running');
    track.replaceChildren(group);
    group.replaceChildren();

    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const targetWidth = viewport.clientWidth + (gap * 2);
    let itemCount = 0;
    do {
      group.append(itemTemplate.content.cloneNode(true));
      itemCount += 1;
    } while (group.getBoundingClientRect().width < targetWidth && itemCount < 100);

    const clone = group.cloneNode(true);
    clone.classList.remove('js-running-ribbon-group');
    clone.setAttribute('aria-hidden', 'true');
    track.append(clone);

    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const distanceRem = (group.getBoundingClientRect().width + gap) / rootFontSize;
    const tokenSpeed = Number.parseFloat(
      getComputedStyle(ribbon).getPropertyValue('--motion-running-ribbon-speed'),
    );
    const configuredSpeed = Number.parseFloat(ribbon.dataset.ribbonSpeed ?? '');
    const speed = Number.isFinite(configuredSpeed) && configuredSpeed > 0
      ? configuredSpeed
      : Number.isFinite(tokenSpeed) && tokenSpeed > 0
        ? tokenSpeed
        : 4;

    track.style.setProperty('--c-running-ribbon-distance', `${distanceRem.toFixed(4)}rem`);
    track.style.setProperty('--c-running-ribbon-duration', `${(distanceRem / speed).toFixed(3)}s`);
    ribbon.dataset.ribbonItemCount = String(itemCount);
    ribbon.dataset.ribbonMode = 'loop';
    ribbon.dataset.ribbonReady = 'true';
    ribbon.classList.add('is-running');
    applyHoverPlaybackRate();
  };

  const scheduleLoopBuild = () => {
    window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(buildLoop);
  };

  if ('ResizeObserver' in window) {
    const resizeObserver = new ResizeObserver(scheduleLoopBuild);
    resizeObserver.observe(viewport);
  } else {
    window.addEventListener('resize', scheduleLoopBuild);
  }

  if ('IntersectionObserver' in window) {
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      ribbon.classList.toggle('is-paused', !entry.isIntersecting);
    }, { threshold: 0.01 });
    intersectionObserver.observe(ribbon);
  }

  viewport.addEventListener('pointerenter', () => {
    hoverSlowed = true;
    applyHoverPlaybackRate();
  });
  viewport.addEventListener('pointerleave', () => {
    hoverSlowed = false;
    applyHoverPlaybackRate();
  });

  if (motionQuery.addEventListener) motionQuery.addEventListener('change', scheduleLoopBuild);
  else motionQuery.addListener(scheduleLoopBuild);
  if (hoverQuery.addEventListener) hoverQuery.addEventListener('change', applyHoverPlaybackRate);
  else hoverQuery.addListener(applyHoverPlaybackRate);

  runningRibbonStates.set(ribbon, { scheduleLoopBuild });
  document.fonts?.ready.then(scheduleLoopBuild);
  scheduleLoopBuild();
}

function initializeRunningRibbons(root = document) {
  if (root instanceof Element && root.matches('.js-running-ribbon')) {
    initializeRunningRibbon(root);
  }
  root.querySelectorAll?.('.js-running-ribbon').forEach(initializeRunningRibbon);
}

function setCartDrawer(open, { trigger = cartToggle } = {}) {
  if (!cartDrawer || !cartToggle) return;
  const wasOpen = cartDrawer.classList.contains('is-open');
  const restoreTarget = lastOverlayTrigger ?? cartToggle;
  if (open) {
    setSiteMenu(false, { restoreFocus: false });
    cancelOverlayClose(cartDrawer);
    cartBackdrop?.classList.remove('is-closing');
    cartDrawer.classList.add('is-open');
    cartBackdrop?.classList.add('is-open');
  } else if (wasOpen) {
    cancelOverlayClose(cartDrawer);
    cartDrawer.classList.remove('is-open');
    cartDrawer.classList.add('is-closing');
    cartBackdrop?.classList.remove('is-open');
    cartBackdrop?.classList.add('is-closing');
  } else {
    return;
  }
  cartDrawer.setAttribute('aria-hidden', String(!open));
  cartDrawer.toggleAttribute('inert', !open);
  cartToggle.setAttribute('aria-expanded', String(open));
  cartToggle.setAttribute('aria-label', open ? 'Close cart' : 'Open cart');
  if (open) {
    lastOverlayTrigger = trigger;
    focusFirstOverlayControl(cartDrawer);
  }
  syncOverlayState();
  if (!open) {
    finishOverlayCloseAfterTransition(cartDrawer, cartDrawer, () => {
      cartBackdrop?.classList.remove('is-closing');
      window.requestAnimationFrame(() => {
        restoreTarget?.focus({ preventScroll: true });
        if (lastOverlayTrigger === restoreTarget) lastOverlayTrigger = null;
      });
    });
  }
}

function setSiteMenu(open, { restoreFocus = true } = {}) {
  if (!siteMenu || !menuToggle) return;
  const wasOpen = siteMenu.classList.contains('is-open');
  if (open) {
    setCartDrawer(false);
    cancelOverlayClose(siteMenu);
    siteMenu.classList.add('is-open');
  } else if (wasOpen) {
    collapseLocalizationForms({ reset: true });
    cancelOverlayClose(siteMenu);
    siteMenu.classList.remove('is-open');
    siteMenu.classList.add('is-closing');
  } else {
    return;
  }
  siteMenu.setAttribute('aria-hidden', String(!open));
  siteMenu.toggleAttribute('inert', !open);
  menuToggle.setAttribute('aria-expanded', String(open));
  menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  document.documentElement.classList.toggle('has-menu-open', open);
  if (open) {
    lastOverlayTrigger = menuToggle;
    focusFirstOverlayControl(siteMenu, siteMenu.querySelector('.c-site-menu__panel'));
  } else if (wasOpen && restoreFocus) {
    menuToggle.focus();
  }
  syncOverlayState();
  if (!open) {
    const panel = siteMenu.querySelector('.c-site-menu__panel');
    if (panel) finishOverlayCloseAfterTransition(siteMenu, panel, () => {});
  }
}

if (params.get('cart') === 'open') {
  setCartDrawer(true);
}

if (params.get('menu') === 'open') {
  setSiteMenu(true, { restoreFocus: false });
}

cartToggle?.addEventListener('click', () => {
  setCartDrawer(cartToggle.getAttribute('aria-expanded') !== 'true');
});

menuToggle?.addEventListener('click', () => {
  setSiteMenu(menuToggle.getAttribute('aria-expanded') !== 'true');
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    const expandedLocalization = localizationForms.find((form) => form.classList.contains('is-expanded'));
    if (expandedLocalization) {
      event.preventDefault();
      setLocalizationExpanded(expandedLocalization, false, { restoreFocus: true, reset: true });
      return;
    }
    setCartDrawer(false);
    setSiteMenu(false);
    lastOverlayTrigger = null;
  }

  if (event.key !== 'Tab') return;
  const openOverlay = siteMenu?.classList.contains('is-open')
    ? siteMenu
    : cartDrawer?.classList.contains('is-open') ? cartDrawer : null;
  const focusable = getFocusable(openOverlay);
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
});

cartBackdrop?.addEventListener('click', () => setCartDrawer(false));

siteMenu?.addEventListener('click', (event) => {
  if (event.target === siteMenu) setSiteMenu(false);
});

function formatMoney(cents) {
  const format = window.stufTheme?.moneyFormat ?? '{{amount}}';
  const amount = Number(cents ?? 0) / 100;
  const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] ?? 'amount';
  const options = placeholder.includes('no_decimals')
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  const locale = placeholder.includes('comma_separator') ? 'de-DE' : 'en-US';
  const formatted = amount.toLocaleString(locale, options);
  const wrapper = document.createElement('span');
  wrapper.innerHTML = format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  return wrapper.textContent.trim();
}

function setCartItemTitle(title, productName, variantTitle = '', quantity = 1) {
  const variant = variantTitle && variantTitle !== 'Default Title' ? variantTitle : '';
  const name = document.createElement('span');
  name.className = 'c-cart-item__product-name';
  name.textContent = `${productName}${variant ? ':' : ''}`;
  title.replaceChildren(name);
  const quantityLabel = quantity > 1 ? document.createElement('span') : null;
  if (quantityLabel) {
    quantityLabel.className = 'c-cart-item__quantity';
    quantityLabel.setAttribute('aria-label', `Quantity ${quantity}`);
    quantityLabel.textContent = `×${quantity}`;
  }
  if (variant) {
    const variantGroup = document.createElement('span');
    variantGroup.className = 'c-cart-item__variant u-wordgroup';
    variantGroup.textContent = variant;
    if (quantityLabel) variantGroup.append(quantityLabel);
    title.append(variantGroup);
  } else if (quantityLabel) {
    quantityLabel.classList.add('u-wordgroup');
    title.append(quantityLabel);
  }
}

function createCartItem(item) {
  const row = document.createElement('a');
  row.className = 'c-cart-item';
  row.href = item.url;
  row.dataset.cartLine = item.key;

  if (item.image) {
    const image = document.createElement('img');
    image.className = 'c-cart-item__image';
    image.src = item.image;
    image.width = 320;
    image.height = Math.round(320 / (item.featured_image?.aspect_ratio || 0.8));
    image.loading = 'lazy';
    image.alt = item.featured_image?.alt || `${item.product_title}${item.variant_title ? `: ${item.variant_title}` : ''}`;
    row.append(image);
  }

  const copy = document.createElement('div');
  copy.className = 'c-cart-item__copy';
  const sku = document.createElement('p');
  sku.className = 'c-cart-item__sku u-trim';
  sku.textContent = item.sku || item.handle?.toUpperCase() || '';
  const title = document.createElement('p');
  title.className = 'c-cart-item__title u-trim';
  setCartItemTitle(title, item.product_title, item.variant_title, item.quantity);
  copy.append(sku, title);

  const price = document.createElement('p');
  price.className = 'c-cart-item__price';
  const priceValue = document.createElement('span');
  priceValue.className = 'u-wordgroup u-trim';
  priceValue.textContent = formatMoney(item.final_line_price);
  price.append(priceValue);
  row.append(copy, price);
  return row;
}

function renderCart(cart) {
  if (!cartItems) return;
  cartItems.replaceChildren();
  if (cart.item_count === 0) {
    const empty = document.createElement('p');
    empty.className = 'c-cart-drawer__empty u-trim';
    empty.textContent = 'No item in cart';
    cartItems.append(empty);
  } else {
    cart.items.forEach((item) => cartItems.append(createCartItem(item)));
  }
  updateShippingProgress(cart);
}

async function getCart() {
  const root = window.stufTheme?.routes?.root ?? '/';
  const response = await fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
  return response.json();
}

// ช่องแจ้งเหตุใน cart drawer — ต้องมองเห็นได้ ไม่ใช่ซ่อนใน aria-label เท่านั้น
const cartNotice = cartDrawer?.querySelector('.js-cart-notice');

function setCartNotice(message, tone = 'error') {
  if (!cartNotice) return;
  cartNotice.textContent = message ?? '';
  cartNotice.hidden = !message;
  cartNotice.classList.toggle('is-info', Boolean(message) && tone === 'info');
  // `role="status"` พอสำหรับ info; เหตุที่เพิ่มของไม่สำเร็จต้องเป็น `alert` ให้ screen reader ตัดคิวอ่าน
  cartNotice.setAttribute('role', tone === 'info' ? 'status' : 'alert');
}

// tier เดียวกับ `snippets/made-to-order-note.liquid` — ถ้าแก้ที่ใดที่หนึ่งต้องแก้ทั้งคู่
function madeToOrderMessage(quantityToPrint) {
  const mto = window.stufTheme?.madeToOrder;
  if (!mto?.enabled || !mto.message || !(quantityToPrint > 0)) return null;
  let days = mto.daysSmall ?? 3;
  if (quantityToPrint > 5) days = mto.daysLarge ?? 7;
  else if (quantityToPrint > 2) days = mto.daysMid ?? 5;
  return String(mto.message).replace('[days]', String(days));
}

async function addVariantToCart(variantId, trigger) {
  if (!variantId || !trigger || trigger.disabled) return;
  const originalLabel = trigger.textContent;
  trigger.disabled = true;
  trigger.classList.add('is-loading');
  trigger.setAttribute('aria-busy', 'true');
  setShippingProgressLoading(true);
  try {
    const response = await fetch(window.stufTheme?.routes?.cartAdd ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      // Cart AJAX API ส่งข้อความมาได้หลาย key แล้วแต่เคส — `errors` คือตัวที่ 422 ใช้จริง
      // ("The maximum quantity of this item is already in your cart.")
      // เดิมอ่านแค่ `description` ทำให้ข้อความจริงหายกลายเป็น "Add to cart failed (422)"
      const reason = payload.description || payload.errors || payload.message;
      throw new Error(typeof reason === 'string' && reason ? reason : `Add to cart failed (${response.status})`);
    }
    const cart = await getCart();
    // ถ้า variant นี้ไม่มีของในสต็อกแล้ว ต้องบอกลูกค้าว่าเป็นการสั่งผลิต
    // จำนวนที่ต้องพิมพ์ = จำนวนในตะกร้าของบรรทัดนี้ (สต็อกเป็น 0 อยู่แล้ว)
    let mtoMessage = null;
    if (trigger.dataset.madeToOrder === 'true') {
      const line = (cart?.items ?? []).find((item) => String(item.variant_id) === String(variantId));
      mtoMessage = madeToOrderMessage(line?.quantity ?? 1);
    }
    setCartNotice(mtoMessage, 'info');
    renderCart(cart);
    setCartDrawer(true, { trigger });
  } catch (error) {
    // ผู้ใช้กดแล้วต้องมีอะไรเกิดขึ้นเสมอ — เดิมเพิ่มไม่สำเร็จแล้ว drawer ไม่เปิด
    // และ `is-error` บนปุ่ม `.c-product-card__add` ก็ไม่มี style รองรับ จึงเงียบสนิท
    // ตอนนี้เปิด drawer ให้เห็นสถานะตะกร้าจริง พร้อมข้อความที่มองเห็นได้
    setCartNotice(error.message);
    try {
      renderCart(await getCart());
    } catch (cartError) {
      // อ่านตะกร้าไม่ได้ก็ยังต้องเปิด drawer ให้เห็นข้อความ
    }
    setCartDrawer(true, { trigger });
    // `.c-product-card.is-error` มี style อยู่แล้ว ส่วนตัวปุ่มไม่มี จึงต้องมาร์คที่ card ด้วย
    const card = trigger.closest('.c-product-card');
    card?.classList.add('is-error');
    trigger.classList.add('is-error');
    trigger.setAttribute('aria-label', error.message);
    window.setTimeout(() => {
      trigger.classList.remove('is-error');
      card?.classList.remove('is-error');
    }, 3000);
  } finally {
    trigger.disabled = false;
    trigger.classList.remove('is-loading');
    trigger.removeAttribute('aria-busy');
    setShippingProgressLoading(false);
    if (originalLabel) trigger.textContent = originalLabel;
  }
}

productForm?.addEventListener('submit', (event) => {
  event.preventDefault();
  const variantId = productForm.querySelector('.js-product-variant-id')?.value;
  void addVariantToCart(variantId, addToCart);
});

quickBuyButtons.forEach((button) => {
  button.addEventListener('click', () => {
    void addVariantToCart(button.dataset.variantId, button);
  });
});

const swatches = [...document.querySelectorAll('.c-swatch')];
const swatchVariantLabels = {
  'marine-blue': 'Marine Blue',
  'lemon-yellow': 'Lemon Yellow',
  'concrete-grey': 'Concrete Grey',
  'gypsum-white': 'Gypsum White',
};

function selectSwatch(swatch) {
  if (!swatch) return;
  swatches.forEach((item) => {
    item.classList.remove('is-selected');
    item.setAttribute('aria-pressed', 'false');
  });
  swatch.classList.add('is-selected');
  swatch.setAttribute('aria-pressed', 'true');
  const label = document.querySelector('.c-product-swatches__label');
  if (label) label.textContent = swatch.getAttribute('aria-label')?.toUpperCase() ?? '';
  const variantId = swatch.dataset.variantId;
  const variantInput = document.querySelector('.js-product-variant-id');
  if (variantId && variantInput) {
    variantInput.value = variantId;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
    const variantData = document.querySelector('.js-product-variants');
    const variants = variantData ? JSON.parse(variantData.textContent) : [];
    const variant = variants.find((item) => String(item.id) === String(variantId));
    const price = document.querySelector('.js-product-price');
    const comparePrice = document.querySelector('.js-product-compare-price');
    const sku = document.querySelector('.js-product-sku');
    if (price && variant) {
      price.textContent = formatMoney(variant.price);
      price.setAttribute('aria-label', `${formatMoney(variant.price)} ${window.stufTheme?.currencyIso ?? ''}`.trim());
    }
    if (comparePrice && variant) {
      const isSale = Number(variant.compare_at_price) > Number(variant.price);
      comparePrice.hidden = !isSale;
      comparePrice.textContent = isSale ? formatMoney(variant.compare_at_price) : '';
    }
    if (sku && variant) sku.textContent = variant.sku || sku.textContent;
    // ข้อความสั่งผลิตต้องตามสีที่เลือก — แต่ละสีมีสต็อกไม่เท่ากัน
    // Liquid render ไว้ครบทุก variant แล้ว ที่นี่แค่สลับว่าโชว์ตัวไหน
    // ห้ามคำนวณจาก `variant.inventory_quantity` ของ JSON — ฟิลด์นั้นไม่มีมาให้
    document.querySelectorAll('.js-made-to-order-option').forEach((option) => {
      option.hidden = String(option.dataset.variantId) !== String(variantId);
    });
    const featuredMediaId = variant?.featured_media?.id ?? variant?.featured_image?.id;
    const productGallery = document.querySelector('.c-product-gallery');
    if (featuredMediaId && productGallery) {
      const gallerySlides = [...productGallery.querySelectorAll('[data-gallery-slide]')];
      const mediaIndex = gallerySlides.findIndex((slide) => String(slide.dataset.mediaId) === String(featuredMediaId));
      if (mediaIndex >= 0) {
        productGallery.querySelector(`[data-gallery-index="${mediaIndex}"]`)?.click();
      }
    }
    if (addToCart && variant) {
      addToCart.disabled = !variant.available;
      addToCart.toggleAttribute('aria-disabled', !variant.available);
      addToCart.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
    }
  }
}

swatches.forEach((swatch) => {
  swatch.addEventListener('click', () => {
    selectSwatch(swatch);
  });
});

const requestedVariant = swatchVariantLabels[params.get('variant')];
if (requestedVariant) {
  selectSwatch(swatches.find((swatch) => swatch.getAttribute('aria-label') === requestedVariant));
}

function setNumberSwitchLoading(control, loading) {
  control.classList.toggle('is-loading', loading);
  control.setAttribute('aria-busy', String(loading));
  const quantity = Number.parseInt(control.dataset.quantity ?? '1', 10);
  control.querySelectorAll('.js-number-switch-step').forEach((button) => {
    const atMinimum = Number.parseInt(button.dataset.step ?? '0', 10) < 0 && quantity <= 1;
    button.disabled = loading || atMinimum;
    button.setAttribute('aria-disabled', String(button.disabled));
  });
}

async function animateNumberSwitchValue(control, quantity) {
  const digit = control.querySelector('.js-number-switch-value');
  if (!digit || digit.textContent.trim() === String(quantity)) return;
  const style = getComputedStyle(control);
  const travel = style.getPropertyValue('--motion-number-slot-travel').trim() || '120%';
  const exit = travel.startsWith('-') ? travel : `-${travel}`;
  const bounce = style.getPropertyValue('--motion-number-slot-bounce').trim() || '-12%';
  const outgoingDuration = cssTimeToMilliseconds(style.getPropertyValue('--duration-number-slot-out'));
  const incomingDuration = cssTimeToMilliseconds(style.getPropertyValue('--duration-number-slot-in'));
  const easing = style.getPropertyValue('--ease-standard').trim() || 'ease';
  control.classList.add('is-changing');
  try {
    const outgoing = digit.animate(
      [{ transform: 'translateY(0)' }, { transform: `translateY(${exit})` }],
      { duration: outgoingDuration, easing, fill: 'forwards' },
    );
    await outgoing.finished.catch(() => {});
    outgoing.cancel();
    digit.textContent = String(quantity);
    const incoming = digit.animate(
      [
        { transform: `translateY(${travel})`, offset: 0 },
        { transform: `translateY(${bounce})`, offset: 0.76 },
        { transform: 'translateY(0)', offset: 1 },
      ],
      { duration: incomingDuration, easing, fill: 'forwards' },
    );
    await incoming.finished.catch(() => {});
    incoming.cancel();
  } finally {
    control.classList.remove('is-changing');
  }
}

function triggerNumberSwitchShake(control, message) {
  window.clearTimeout(numberSwitchErrorTimers.get(control));
  const status = control.querySelector('.js-number-switch-status');
  if (status) status.textContent = message || 'Maximum available quantity reached';
  control.classList.remove('has-error');
  void control.offsetWidth;
  control.classList.add('has-error');
  const duration = cssTimeToMilliseconds(
    getComputedStyle(control).getPropertyValue('--duration-number-feedback'),
  );
  numberSwitchErrorTimers.set(control, window.setTimeout(() => {
    control.classList.remove('has-error');
    numberSwitchErrorTimers.delete(control);
  }, duration));
}

function replaceCartSection(sectionShell, sectionId, html) {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  const escapedId = window.CSS?.escape ? CSS.escape(sectionId) : sectionId;
  const replacement = template.content.querySelector(`#shopify-section-${escapedId}`);
  if (replacement) sectionShell.replaceWith(replacement);
  else sectionShell.replaceChildren(template.content);
}

async function changeCartQuantity(control, quantity, trigger) {
  if (control.classList.contains('is-loading')) return;
  const line = Number.parseInt(control.dataset.cartLine ?? '0', 10);
  const safeQuantity = Math.max(1, quantity);
  const form = control.closest('.js-cart-form');
  const sectionShell = form?.closest('.shopify-section');
  const sectionId = sectionShell?.id.replace(/^shopify-section-/, '') ?? '';
  if (!line || !form) return;

  control.classList.remove('has-error');
  const status = control.querySelector('.js-number-switch-status');
  if (status) status.textContent = '';
  setNumberSwitchLoading(control, true);
  setShippingProgressLoading(true);
  try {
    const response = await fetch(window.stufTheme?.routes?.cartChange ?? '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        line,
        quantity: safeQuantity,
        sections: sectionId ? [sectionId] : undefined,
        sections_url: window.location.pathname,
      }),
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(cart.description || `Cart update failed (${response.status})`);

    const updatedQuantity = cart.items?.[line - 1]?.quantity ?? safeQuantity;
    await animateNumberSwitchValue(control, updatedQuantity);

    const sectionHtml = sectionId ? cart.sections?.[sectionId] : '';
    if (!sectionShell || !sectionHtml) {
      window.location.reload();
      return;
    }

    replaceCartSection(sectionShell, sectionId, sectionHtml);
    renderCart(cart);
    page.classList.toggle('is-cart-populated', cart.item_count > 0 && page.classList.contains('c-page-cart'));
    window.requestAnimationFrame(() => {
      const nextControl = document.querySelector(`.js-number-switch[data-cart-line="${line}"]`);
      const nextTrigger = nextControl?.querySelector(`.js-number-switch-step[data-step="${trigger.dataset.step}"]`);
      nextTrigger?.focus({ preventScroll: true });
    });
  } catch (error) {
    setNumberSwitchLoading(control, false);
    triggerNumberSwitchShake(control, error.message);
  } finally {
    setShippingProgressLoading(false);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const trigger = event.target.closest('.js-number-switch-step');
  if (!trigger || trigger.disabled) return;
  const control = trigger.closest('.js-number-switch');
  if (!control) return;
  const quantity = Number.parseInt(control.dataset.quantity ?? '0', 10);
  const step = Number.parseInt(trigger.dataset.step ?? '0', 10);
  const nextQuantity = Math.max(0, quantity + step);
  if (nextQuantity === quantity) return;
  void changeCartQuantity(control, nextQuantity, trigger);
});

localizationForms.forEach((form) => {
  document.fonts?.ready.then(() => syncLocalizationOverflow(form));
  if ('ResizeObserver' in window) {
    new ResizeObserver(() => syncLocalizationOverflow(form)).observe(form);
  }
  form.querySelector('.js-localization-trigger')?.addEventListener('click', () => {
    setLocalizationExpanded(form, true, { restoreFocus: false });
  });
  form.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('.js-localization-radio')) {
      syncLocalizationOptions(form);
    }
  });
  form.addEventListener('submit', (event) => {
    const isLocalThemePreview = ['127.0.0.1', 'localhost', '::1'].includes(window.location.hostname);
    if (!isLocalThemePreview) return;

    const selected = form.querySelector('.js-localization-radio:checked');
    const countryCode = selected?.value.trim().toUpperCase() ?? '';
    if (!/^[A-Z]{2}$/.test(countryCode)) return;

    // Shopify CLI currently returns 401 for POST /localization on its loopback
    // proxy. Its GET country context is server-rendered and sets Shopify's real
    // localization cookie, while non-local storefronts keep the native form POST.
    event.preventDefault();
    const target = new URL(window.location.href);
    target.searchParams.set('country', countryCode);
    target.searchParams.delete('menu');
    window.location.assign(target);
  });
});

initializeRunningRibbons();
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initializeRunningRibbons(event.target);
});

document.querySelectorAll('.js-media-carousel').forEach((gallery) => {
  const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
  const tabs = [...gallery.querySelectorAll('[data-gallery-index]')];
  if (slides.length < 2 || tabs.length !== slides.length) return;
  const progress = gallery.querySelector('.c-media-progress--interactive');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const interval = Number.parseInt(gallery.dataset.galleryInterval ?? '5000', 10);
  const pauseReasons = new Set();
  const preparedSlides = new Map();
  let current = 0;
  let timer;
  let clockStartedAt = 0;
  let remaining = interval;
  let transitionRequest = 0;

  gallery.style.setProperty('--duration-gallery', `${interval}ms`);
  gallery.dataset.galleryClock = 'remaining-time';

  function normalizeIndex(index) {
    return (index + slides.length) % slides.length;
  }

  function prepareSlide(index) {
    const normalizedIndex = normalizeIndex(index);
    if (preparedSlides.has(normalizedIndex)) return preparedSlides.get(normalizedIndex);
    const slide = slides[normalizedIndex];
    const image = slide instanceof HTMLImageElement ? slide : slide.querySelector('img');
    if (!image) {
      slide.dataset.galleryPrepared = 'true';
      return Promise.resolve();
    }
    // สไลด์ที่ไม่ใช่ใบแรกส่ง URL มาใน data-src/data-srcset เพื่อไม่ให้แย่ง bandwidth
    // กับภาพ LCP ตอนโหลดหน้า — เลื่อนขั้นเป็น attribute จริงตอนจะใช้เท่านั้น
    // srcset ต้องมาก่อน src เสมอ ไม่งั้นเบราว์เซอร์เริ่มโหลด candidate ผิดตัว
    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      delete image.dataset.srcset;
    }
    if (image.dataset.src) {
      image.src = image.dataset.src;
      delete image.dataset.src;
    }
    image.loading = 'eager';
    image.decoding = 'async';
    const ready = image.decode()
      .catch(() => {})
      .finally(() => {
        slide.dataset.galleryPrepared = 'true';
      });
    preparedSlides.set(normalizedIndex, ready);
    return ready;
  }

  function clearClock({ preserveRemaining = true } = {}) {
    if (timer !== undefined && preserveRemaining && clockStartedAt) {
      remaining = Math.max(0, remaining - (performance.now() - clockStartedAt));
    }
    window.clearTimeout(timer);
    timer = undefined;
    clockStartedAt = 0;
  }

  function syncPauseState() {
    const isPaused = reducedMotion || pauseReasons.size > 0;
    progress?.classList.toggle('is-paused', isPaused);
    gallery.dataset.galleryPaused = String(isPaused);
  }

  function armClock({ restart = false } = {}) {
    clearClock({ preserveRemaining: false });
    if (restart) remaining = interval;
    syncPauseState();
    if (reducedMotion || pauseReasons.size > 0) return;
    if (remaining <= 0) remaining = interval;
    clockStartedAt = performance.now();
    timer = window.setTimeout(() => {
      timer = undefined;
      clockStartedAt = 0;
      remaining = interval;
      void showSlide(current + 1);
    }, remaining);
  }

  function setPauseReason(reason, active) {
    const changed = active ? !pauseReasons.has(reason) : pauseReasons.has(reason);
    if (!changed) return;
    if (active) {
      clearClock();
      pauseReasons.add(reason);
    } else {
      pauseReasons.delete(reason);
    }
    syncPauseState();
    if (!reducedMotion && pauseReasons.size === 0) armClock();
  }

  function commitSlide(nextIndex) {
    current = normalizeIndex(nextIndex);
    slides.forEach((slide, index) => {
      slide.classList.toggle('is-current', index === current);
      slide.setAttribute('aria-hidden', String(index !== current));
    });
    tabs.forEach((tab, index) => {
      tab.classList.toggle('is-current', index === current);
      tab.classList.toggle('is-complete', index < current);
      tab.setAttribute('aria-selected', String(index === current));
    });
  }

  async function showSlide(nextIndex) {
    const requestedIndex = normalizeIndex(nextIndex);
    const request = ++transitionRequest;
    if (requestedIndex === current) {
      setPauseReason('transition', false);
      return;
    }
    setPauseReason('transition', true);
    await prepareSlide(requestedIndex);
    if (request !== transitionRequest) return;
    commitSlide(requestedIndex);
    remaining = interval;
    setPauseReason('transition', false);
    void prepareSlide(current + 1);
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => void showSlide(Number.parseInt(tab.dataset.galleryIndex, 10)));
  });
  gallery.querySelector('.js-gallery-previous')?.addEventListener('click', () => void showSlide(current - 1));
  gallery.querySelector('.js-gallery-next')?.addEventListener('click', () => void showSlide(current + 1));
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target instanceof Element
      ? event.target.closest('[data-gallery-slide]')
      : null;
    const blockIndex = block ? slides.indexOf(block) : -1;
    if (blockIndex < 0 || !gallery.contains(block)) return;
    setPauseReason('theme-editor', true);
    void showSlide(blockIndex);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const block = event.target instanceof Element
      ? event.target.closest('[data-gallery-slide]')
      : null;
    if (!block || !gallery.contains(block)) return;
    setPauseReason('theme-editor', false);
  });
  gallery.addEventListener('mouseenter', () => setPauseReason('pointer', true));
  gallery.addEventListener('mouseleave', () => setPauseReason('pointer', false));
  gallery.addEventListener('focusin', () => setPauseReason('focus', true));
  gallery.addEventListener('focusout', (event) => {
    if (gallery.contains(event.relatedTarget)) return;
    setPauseReason('focus', false);
  });
  document.addEventListener('visibilitychange', () => {
    setPauseReason('document-hidden', document.hidden);
  });
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      setPauseReason('off-screen', !entry.isIntersecting);
    }, { threshold: 0.1 });
    observer.observe(gallery);
  }

  if (reducedMotion) pauseReasons.add('reduced-motion');
  if (document.hidden) pauseReasons.add('document-hidden');
  pauseReasons.add('initializing');
  commitSlide(0);
  syncPauseState();
  void prepareSlide(0).then(() => {
    void prepareSlide(1);
    remaining = interval;
    setPauseReason('initializing', false);
  });
});
